import { useEffect } from 'react';
import { useSession } from 'next-auth/react';

const FRONTCHAT_ID = '4b18fc0acfa2c45fe07a946e62ecd5c3';
const FRONTCHAT_SECRET = '6640839fca07aee4a970e7d692b68f98';

declare global {
  interface Window {
    FrontChat?: (command: string, options?: Record<string, unknown>) => void;
  }
}

interface FrontChatUser {
  id: string;
  name: string;
  email: string;
}

async function createUserHash(userId: string): Promise<string | null> {
  try {
    const encoder = new TextEncoder();
    const key = await window.crypto.subtle.importKey(
      'raw',
      encoder.encode(FRONTCHAT_SECRET),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    const signature = await window.crypto.subtle.sign('HMAC', key, encoder.encode(userId));
    const hashArray = Array.from(new Uint8Array(signature));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('Failed to create FrontChat verification hash:', error);
    return null;
  }
}

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.FrontChat) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://chat-assets.frontapp.com/v1/chat.bundle.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load FrontChat script'));
    document.body.appendChild(script);
  });
}

export default function FrontChat() {
  const { data: session } = useSession();

  useEffect(() => {
    // Runtime user object has `id` (not `sub` as typed in authenticationUser.ts)
    const user = session?.user as unknown as FrontChatUser | undefined;
    if (!user?.id) return;

    let isCancelled = false;

    async function init() {
      try {
        await loadScript();
        if (isCancelled) return;

        const userHash = await createUserHash(user!.id);
        if (isCancelled || !window.FrontChat) return;

        window.FrontChat('init', {
          chatId: FRONTCHAT_ID,
          useDefaultLauncher: true,
          name: user!.name || '',
          userId: user!.id,
          userHash,
          contact: { email: user!.email },
        });

        const iframe = document.getElementById('front-chat-iframe');
        if (iframe) {
          iframe.style.zIndex = '1';
        }
      } catch (error) {
        console.error('Failed to initialize FrontChat:', error);
      }
    }

    init();

    return () => {
      isCancelled = true;
      if (window.FrontChat) {
        window.FrontChat('shutdown');
      }
    };
  }, [session?.user]);

  return null;
}
